import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { captureAttribution } from "@/lib/attribution";
import { initPixel, pixelTrack } from "@/lib/pixel";
import { trackFunnel } from "@/lib/track";
import {
  Hero, Opportunity, Pillars, Products, AgentNetwork, Automation, SelfFunding,
  Comparison, Founder, WhatYouGet, WhoCanStart, VideoSection, OfficeSection, DocumentsSection, Faq, FinalCta,
} from "@/components/landing/sections";
import { Landmark, Menu, X, ArrowRight, Phone, Mail, MapPin } from "lucide-react";

function scrollToForm() {
  const el = document.getElementById("lead-form") || document.getElementById("hero");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function LandingPage() {
  const [content, setContent] = useState(null);
  const [company, setCompany] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const { slug } = useParams();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    captureAttribution();
    api.get(slug ? `/public/p/${slug}` : "/public/landing").then((r) => setContent(r.data)).catch(() => { if (slug) setNotFound(true); });
    api.get("/public/company").then((r) => setCompany(r.data || {})).catch(() => {});
    api.get("/public/tracking").then((r) => {
      if (r.data?.enabled && r.data?.pixel_id) {
        initPixel(r.data.pixel_id);
        pixelTrack("PageView");
        pixelTrack("ViewContent", { content_name: "Own Brand Loan Agency" });
      }
    }).catch(() => {});
    trackFunnel("LANDING_PAGE_VIEW");
    trackFunnel("VIEW_CONTENT");
  }, []);

  // SEO
  useEffect(() => {
    if (!content?.seo) return;
    document.title = content.seo.title || "Paisapaid";
    const setMeta = (name, val, prop = false) => {
      if (!val) return;
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let m = document.querySelector(sel);
      if (!m) { m = document.createElement("meta"); prop ? m.setAttribute("property", name) : m.setAttribute("name", name); document.head.appendChild(m); }
      m.setAttribute("content", val);
    };
    setMeta("description", content.seo.meta_description);
    setMeta("og:title", content.seo.og_title, true);
    setMeta("og:description", content.seo.og_description, true);
    setMeta("og:image", content.seo.og_image, true);
  }, [content]);

  const onCta = useCallback(() => { trackFunnel("CTA_CLICK"); pixelTrack("Contact"); scrollToForm(); }, []);

  if (notFound) {
    return (
      <div className="app-bg min-h-screen flex flex-col items-center justify-center text-center px-6">
        <Landmark className="h-10 w-10 text-primary mb-4" />
        <h1 className="font-heading text-2xl font-bold">Page not found</h1>
        <p className="text-slate-400 mt-2">This landing page is not available or hasn't been published yet.</p>
        <Link to="/" className="mt-6 text-primary">Go to homepage</Link>
      </div>
    );
  }

  if (!content) {
    return <div className="app-bg min-h-screen flex items-center justify-center"><Landmark className="h-8 w-8 text-primary animate-pulse" /></div>;
  }

  const nav = content.nav || {};
  const brand = company.company_name || "Paisapaid";

  return (
    <div className="app-bg min-h-screen pb-20 lg:pb-0">
      {/* NAV */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-2.5">
            {company.logo ? <img src={company.logo} alt={brand} className="h-8 w-auto" /> :
              <span className="h-9 w-9 rounded-lg bg-primary/15 flex items-center justify-center"><Landmark className="h-5 w-5 text-primary" /></span>}
            <span className="font-heading font-extrabold text-lg">{brand}</span>
          </a>
          <nav className="hidden lg:flex items-center gap-7">
            {(nav.items || []).map((it) => (
              <a key={it.target} href={`#${it.target}`} className="text-sm text-slate-300 hover:text-primary transition-colors">{it.label}</a>
            ))}
            <button onClick={onCta} data-testid="nav-cta" className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm px-4 py-2 hover:bg-primary/90 transition-colors">
              {nav.cta} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </nav>
          <button className="lg:hidden text-foreground" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-slate-800 bg-slate-950/95 px-5 py-4 space-y-3">
            {(nav.items || []).map((it) => (
              <a key={it.target} href={`#${it.target}`} onClick={() => setMenuOpen(false)} className="block text-slate-300 py-1">{it.label}</a>
            ))}
          </div>
        )}
      </header>

      <Hero c={content} onCta={onCta} hideForm={!!slug} />
      <Opportunity c={content} />
      <Pillars c={content} />
      <Products c={content} />
      <AgentNetwork c={content} />
      <Automation c={content} />
      <SelfFunding c={content} />
      <Comparison c={content} />
      <Founder c={content} />
      <WhatYouGet c={content} />
      <WhoCanStart c={content} />
      <VideoSection c={content} />
      <OfficeSection c={content} />
      <DocumentsSection c={content} />
      <Faq c={content} />
      <FinalCta c={content} hideForm={!!slug} />

      {/* FOOTER */}
      <footer id="footer" className="border-t border-slate-800 px-5 sm:px-8 py-12">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="h-9 w-9 rounded-lg bg-primary/15 flex items-center justify-center"><Landmark className="h-5 w-5 text-primary" /></span>
              <span className="font-heading font-extrabold text-lg">{brand}</span>
            </div>
            {company.tagline && <p className="mt-3 text-sm text-slate-400">{company.tagline}</p>}
          </div>
          <div>
            <h4 className="font-semibold mb-3 text-sm">Contact</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              {company.phone && <li className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{company.phone}</li>}
              {company.email && <li className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />{company.email}</li>}
              {company.address && <li className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5" />{company.address}</li>}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-3 text-sm">Legal</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/legal/privacy" className="hover:text-primary">Privacy Policy</Link></li>
              <li><Link to="/legal/terms" className="hover:text-primary">Terms &amp; Conditions</Link></li>
              <li><Link to="/legal/disclaimer" className="hover:text-primary">Disclaimer</Link></li>
              <li><Link to="/legal/refund" className="hover:text-primary">Refund Policy</Link></li>
              <li><Link to="/legal/contact" className="hover:text-primary">Contact Us</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-3 text-sm">Access</h4>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><Link to="/login" className="hover:text-primary">Login</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-slate-800/70">
          <p className="text-xs text-slate-500 leading-relaxed">{content.disclaimer}</p>
          <p className="text-xs text-slate-600 mt-4">© {new Date().getFullYear()} {brand}. All rights reserved.</p>
        </div>
      </footer>

      {/* STICKY MOBILE CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-slate-950/90 backdrop-blur-md border-t border-slate-800">
        <button onClick={onCta} data-testid="sticky-mobile-cta"
          className="w-full rounded-xl bg-primary text-primary-foreground font-bold py-3.5 flex items-center justify-center gap-2">
          {nav.cta} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
